import Sidebar from './Sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAdminPatients } from '../../actions/patientActions';
import { getAllStaff } from '../../actions/staffActions';
import { getAllBills } from '../../actions/billingActions';
import { getDaySummary } from '../../actions/accountActions';
import { toInputDate } from '../../utils/formatDate';

const currency = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function StatCard({ to, icon, iconBg, title, value, sub }) {
  return (
    <div className="col-xl-3 col-sm-6 mb-4">
      <Link to={to} className="text-decoration-none">
        <div className="card dashboard-stat-card h-100">
          <div className="card-body d-flex align-items-center gap-2 gap-sm-3 p-2.5 p-sm-3">
            <div className={`stat-icon ${iconBg}`}>
              <i className={icon}></i>
            </div>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="text-muted small fw-semibold text-uppercase text-truncate" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                {title}
              </div>
              <div className="fs-5 fw-bold text-truncate" title={typeof value === 'string' ? value : ''} style={{ color: 'var(--text-dark)', lineHeight: 1.2, marginTop: '0.15rem' }}>
                {value}
              </div>
              {sub && (
                <div className="small mt-1 text-truncate" style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {sub}
                </div>
              )}
            </div>
            <i className="fa fa-chevron-right d-none d-sm-block" style={{ color: 'var(--border)', fontSize: '0.75rem' }}></i>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function Dashboard() {
  const { patients = [] } = useSelector(state => state.patientsState);
  const { staffList = [] } = useSelector(state => state.staffState);
  const { bills = [] } = useSelector(state => state.billingState);
  const { day } = useSelector(state => state.accountState);
  const { user } = useSelector(state => state.authState);

  const dispatch = useDispatch();
  const isAdmin = user?.role === 'admin' || user?.role === 'master';
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'master';
  const canAddPatient = isAdmin || user?.role === 'doctor';

  useEffect(() => {
    dispatch(getAdminPatients());
    if (isAdminOrManager) {
      dispatch(getAllStaff());
      dispatch(getAllBills());
      dispatch(getDaySummary(toInputDate()));
    }
  }, [dispatch, isAdminOrManager]);

  const pendingDue = bills.reduce((sum, b) => b.status === 'Cancelled' ? sum : sum + (b.balanceDue || 0), 0);
  const activeBills = bills.filter(b => b.status !== 'Cancelled');

  const quickActions = [
    ...(canAddPatient ? [{ to: '/admin/newpatient', label: 'Add Patient', icon: 'fa fa-user-plus' }] : []),
    ...(isAdmin ? [
      { to: '/admin/billing/new', label: 'New Bill', icon: 'fa fa-file-invoice' },
      { to: '/admin/staff/new', label: 'Add Staff', icon: 'fa fa-id-card' },
      { to: '/admin/attendance', label: 'Attendance', icon: 'fa fa-calendar-check' },
    ] : []),
    { to: '/admin/hospital', label: 'Hospital Info', icon: 'fa fa-hospital-alt' },
    { to: '/admin/diets', label: 'Common Diet', icon: 'fa fa-apple-alt' },
  ];

  // All subcategories & configuration modules for fast access (especially on mobile)
  const allModules = [
    {
      to: '/admin/categories',
      label: 'Account Categories',
      desc: 'Income & Expense heads',
      icon: 'fa fa-tags',
      color: '#d97706',
      bg: '#fef3c7',
      tag: 'Accounts',
      adminOnly: true
    },
    {
      to: '/admin/treatments',
      label: 'Treatments & Rates',
      desc: 'Master treatments & pricing',
      icon: 'fa fa-stethoscope',
      color: '#0d9488',
      bg: '#ccfbf1',
      tag: 'Pricing',
      adminOnly: true
    },
    {
      to: '/admin/designations',
      label: 'Staff Designations',
      desc: 'Role titles & staff positions',
      icon: 'fa fa-id-badge',
      color: '#4f46e5',
      bg: '#e0e7ff',
      tag: 'Roles',
      adminOnly: true
    },
    {
      to: '/admin/hospital',
      label: 'Hospital Info & Directory',
      desc: 'Vendor shops, contacts & certificates',
      icon: 'fa fa-hospital-alt',
      color: '#059669',
      bg: '#d1fae5',
      tag: 'Directory',
      adminOnly: false
    },
    {
      to: '/admin/diets',
      label: 'Common Diet Plans',
      desc: 'Diet templates & prescriptions',
      icon: 'fa fa-apple-alt',
      color: '#db2777',
      bg: '#fce7f3',
      tag: 'Diets',
      adminOnly: false
    },
    {
      to: '/admin/staff',
      label: 'Staff Directory',
      desc: 'Active staff & payroll details',
      icon: 'fa fa-users',
      color: '#0284c7',
      bg: '#e0f2fe',
      tag: 'Staff',
      adminOnly: true
    },
    {
      to: '/admin/attendance',
      label: 'Attendance Register',
      desc: 'Daily staff attendance & leave',
      icon: 'fa fa-calendar-check',
      color: '#7c3aed',
      bg: '#ede9fe',
      tag: 'HR',
      adminOnly: true
    },
    {
      to: '/admin/billing',
      label: 'Billing & Invoices',
      desc: 'Invoices, receipts & payments',
      icon: 'fa fa-file-invoice-dollar',
      color: '#ea580c',
      bg: '#ffedd5',
      tag: 'Billing',
      adminOnly: true
    },
    {
      to: '/admin/accounts',
      label: 'Day Book & Accounts',
      desc: 'Daily cash, bank & income registers',
      icon: 'fa fa-wallet',
      color: '#2563eb',
      bg: '#dbeafe',
      tag: 'Finance',
      adminOnly: true
    },
    {
      to: '/admin/users',
      label: 'User Management',
      desc: 'System logins & credentials',
      icon: 'fa fa-users-cog',
      color: '#475569',
      bg: '#f1f5f9',
      tag: 'Security',
      strictAdminOnly: true
    },
    {
      to: '/admin/trash',
      label: 'Trash Bin',
      desc: 'Restore deleted patients & bills',
      icon: 'fa fa-trash-alt',
      color: '#dc2626',
      bg: '#fee2e2',
      tag: 'Recovery',
      strictAdminOnly: true
    }
  ];

  const visibleModules = allModules.filter(m => {
    if (m.strictAdminOnly) return isAdmin;
    if (m.adminOnly) return isAdminOrManager;
    return true;
  });

  return (
    <div className="row g-0">
      <div className="col-12 col-md-2">
        <Sidebar />
      </div>
      <div className="col-12 col-md-10 main-content">
        {/* Page header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>Dashboard</h1>
            <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Welcome back, <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{user?.name || 'there'}</span>
            </p>
          </div>
          <span
            className="badge"
            style={{
              background: 'rgba(13,148,136,0.12)',
              color: 'var(--primary)',
              border: '1px solid rgba(13,148,136,0.25)',
              textTransform: 'capitalize',
              fontWeight: 600,
              padding: '0.45em 0.9em',
              borderRadius: '20px',
              fontSize: '0.78rem',
            }}
          >
            <i className="fa fa-shield-alt me-1"></i>
            {user?.role}
          </span>
        </div>

        {/* Stat cards */}
        <div className="row g-3">
          <StatCard
            to="/patients"
            icon="fa fa-user-injured fa-lg text-white"
            iconBg="bg-success"
            title="Total Patients"
            value={patients.length}
            sub="Total Registered Patients"
          />

          {isAdminOrManager && (
            <>
              <StatCard
                to="/admin/staff"
                icon="fa fa-id-card fa-lg text-white"
                iconBg="bg-info"
                title="Total Staff"
                value={staffList.length}
                sub="Active staff members"
              />
              <StatCard
                to="/admin/billing"
                icon="fa fa-file-invoice-dollar fa-lg text-white"
                iconBg="bg-warning"
                title="Total Invoices"
                value={activeBills.length}
                sub={`₹${pendingDue.toLocaleString()} unpaid due`}
              />
              <StatCard
                to="/admin/accounts"
                icon="fa fa-wallet fa-lg text-white"
                iconBg="bg-primary"
                title="Today's Income"
                value={currency(day?.income)}
                sub={`Expense: ${currency(day?.expense)}`}
              />
            </>
          )}
        </div>

        {/* Quick Actions */}
        {quickActions.length > 0 && (
          <div className="card mb-4">
            <div className="card-body p-3">
              <div className="text-muted small fw-bold text-uppercase mb-3" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>
                Quick Actions
              </div>
              <div className="row g-2">
                {quickActions.map(act => (
                  <div key={act.to} className="col-6 col-md-4 col-lg-2">
                    <Link
                      to={act.to}
                      className="btn btn-light w-100 d-flex align-items-center justify-content-center gap-2 py-2.5 border fw-medium text-truncate"
                      style={{ fontSize: '0.82rem', borderRadius: '8px' }}
                    >
                      <i className={`${act.icon} text-primary`}></i>
                      <span className="text-truncate">{act.label}</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Subcategories & Modules Hub (Mobile-Accessible) ── */}
        <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '14px' }}>
          <div className="card-header bg-white border-0 pt-3 px-3 pb-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.95rem' }}>
                <i className="fa fa-th-large" style={{ color: 'var(--primary, #0d9488)' }}></i>
                Subcategories & Clinic Modules
              </h6>
              <p className="text-muted mb-0 small" style={{ fontSize: '0.78rem' }}>
                Fast access to categories, treatments, designations, hospital directory, and configurations
              </p>
            </div>
            <span className="badge bg-light text-muted border" style={{ fontSize: '0.72rem' }}>
              {visibleModules.length} Modules
            </span>
          </div>
          <div className="card-body p-3">
            <div className="row g-2 g-md-3">
              {visibleModules.map(item => (
                <div key={item.to} className="col-6 col-md-4 col-lg-3">
                  <Link to={item.to} className="text-decoration-none">
                    <div className="card dashboard-module-card h-100 p-2.5 p-md-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '10px',
                          backgroundColor: item.bg, color: item.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <i className={item.icon} style={{ fontSize: '1rem' }}></i>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.84rem' }}>
                            {item.label}
                          </div>
                          {item.tag && (
                            <span className="badge" style={{ fontSize: '0.65rem', backgroundColor: item.bg, color: item.color, padding: '0.2em 0.5em' }}>
                              {item.tag}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-muted mb-0 small" style={{ fontSize: '0.74rem', lineHeight: '1.3' }}>
                        {item.desc}
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Patients Table */}
        <div className="card">
          <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
            <h6 className="mb-0 fw-bold">Recent Registered Patients</h6>
            <Link to="/patients" className="btn btn-sm btn-outline-primary">
              View All Patients <i className="fa fa-arrow-right ms-1"></i>
            </Link>
          </div>
          <div className="card-body p-0">
            {patients.length === 0 ? (
              <div className="text-center p-4 text-muted">No patients registered yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead style={{ background: 'var(--bg-light)' }}>
                    <tr>
                      <th className="ps-3">ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th className="pe-3 text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patients.slice(0, 5).map(p => (
                      <tr key={p._id}>
                        <td className="ps-3 fw-bold text-primary">{p.patientId}</td>
                        <td className="fw-semibold">{p.name}</td>
                        <td>{p.phone}</td>
                        <td className="text-muted">{p.address || '-'}</td>
                        <td className="pe-3 text-end">
                          <Link to={`/patient/${p.patientId || p._id || p.id}`} className="btn btn-sm btn-outline-secondary py-1 px-2">
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
